import { 
  Plus, Pencil, Trash2, Check, X, RefreshCw, Search, Filter, Download, Upload,
  Settings, HelpCircle, ArrowLeft, ArrowRight,
  BarChart3, LineChart, TrendingUp, TrendingDown,
  CheckCircle2, AlertTriangle, XCircle, Info,
  User, Users, UsersRound, FileText, MessageSquare, Mail,
  Brain, Lock, Crown, Zap, Target, Lightbulb, Shield,
  Calendar, Clock, Eye, Send, Share2, Copy, ExternalLink,
  Building, Sparkles, AlertCircle, Minus, Tag, Bell, Home,
  type LucideIcon
} from "lucide-react";

/**
 * Standard icon sizes for consistent usage across the platform
 * Use these instead of hardcoding h-* w-* classes
 */
export const ICON_SIZES = {
  xs: "h-3 w-3",      // Inline text, tiny badges (12px)
  sm: "h-4 w-4",      // Buttons, table cells, navigation (16px)
  md: "h-5 w-5",      // Page headers, card headers, default (20px)
  lg: "h-6 w-6",      // Feature highlights, empty states (24px)
  xl: "h-8 w-8",      // Hero sections, major CTAs (32px)
  "2xl": "h-10 w-10", // Large empty states (40px)
  "3xl": "h-12 w-12", // Hero illustrations (48px)
} as const;

/**
 * Semantic icon mapping for common actions and entities
 * Use these constants instead of importing icons directly for consistency
 */
export const STANDARD_ICONS = {
  // Actions
  create: Plus,
  edit: Pencil,
  delete: Trash2,
  save: Check,
  cancel: X,
  refresh: RefreshCw,
  search: Search,
  filter: Filter,
  download: Download,
  upload: Upload,
  send: Send,
  share: Share2,
  copy: Copy,
  external: ExternalLink,
  
  // Navigation
  settings: Settings,
  help: HelpCircle,
  back: ArrowLeft,
  next: ArrowRight,
  home: Home,
  
  // Analytics & Charts
  analytics: BarChart3,
  chart: LineChart,
  trending: TrendingUp,
  trendingDown: TrendingDown,
  trendingFlat: Minus,
  
  // Status & Feedback
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
  alert: AlertCircle,
  
  // Entities
  user: User,
  users: Users,
  team: UsersRound,
  page: FileText,
  feedback: MessageSquare,
  campaign: Mail,
  company: Building,
  
  // Features
  ai: Brain,
  lock: Lock,
  premium: Crown,
  insight: Sparkles,
  target: Target,
  idea: Lightbulb,
  security: Shield,
  power: Zap,
  
  // Time & Organization
  calendar: Calendar,
  clock: Clock,
  tag: Tag,
  
  // UI Elements
  view: Eye,
  bell: Bell,
} as const;

/**
 * Standard color variants for icons
 * Maps to design system tokens from index.css
 */
export const ICON_COLOR_VARIANTS = {
  primary: "text-primary",
  success: "text-success",
  warning: "text-warning",
  destructive: "text-destructive",
  muted: "text-muted-foreground",
  info: "text-info",
  neutral: "text-muted-foreground",
  purple: "text-primary",
} as const;

/**
 * Helper type for icon sizes
 */
export type IconSize = keyof typeof ICON_SIZES;

/**
 * Helper type for icon variants
 */
export type IconVariant = keyof typeof ICON_COLOR_VARIANTS;

/**
 * Helper function to get icon class names
 */
export const getIconClasses = (size: IconSize = "md", variant?: IconVariant) => {
  const sizeClass = ICON_SIZES[size];
  const variantClass = variant ? ICON_COLOR_VARIANTS[variant] : "";
  return `${sizeClass} ${variantClass}`.trim();
};
